/**
 * ContactAgentModal — bespoke studio UI for the `contactForm` and
 * `agentContactModal` corpus modal ids (openModal). Replaces the generic
 * HostModal for "contact an agent" flows.
 *
 * Forwarded props (from the corpus openModal step, `popup`/`modalId` stripped):
 *   - contactForm        → { agentId, agentName }   (card/agent-card)
 *   - agentContactModal  → { agentId }              (ecommerce/property-detail)
 *
 * Preview behavior: there is no backend in the build-mode canvas, so Submit
 * fires a success toast and closes (parity with the preview-callback toasts in
 * use-studio-host-services). Bound to its modal id by createStudioModalRegistry
 * so `data-modal-id` distinguishes contactForm vs agentContactModal.
 */
import { type FormEvent } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import type { ModalComponentProps } from '@cogo/react-renderer-v2'
import { Button } from '@/components/ui/button'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { toast } from '@/store/toast-store'

export function ContactAgentModal({ modalId, props, close }: ModalComponentProps & { modalId: string }) {
  const agentName = typeof props.agentName === 'string' ? props.agentName : undefined
  const agentId = typeof props.agentId === 'string' ? props.agentId : undefined
  const title = agentName ? `Contact ${agentName}` : 'Contact agent'

  const submit = (e: FormEvent) => {
    e.preventDefault()
    toast.success(agentName ? `Message sent to ${agentName}` : 'Message sent')
    close()
  }

  return (
    <Dialog.Root open onOpenChange={(open) => !open && close()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <Dialog.Content
          data-testid="contact-agent-modal"
          data-modal-id={modalId}
          className="fixed left-1/2 top-1/2 z-50 w-full max-w-[440px] -translate-x-1/2 -translate-y-1/2 rounded-xl border border-border bg-card p-6 text-foreground shadow-xl"
        >
          <Dialog.Title className="mb-1 text-lg font-semibold">{title}</Dialog.Title>
          <Dialog.Description className="mb-4 text-sm text-muted-foreground">
            Send a message{agentName ? ` to ${agentName}` : ''} — we&rsquo;ll get back to you shortly.
          </Dialog.Description>
          {agentId ? (
            <p className="mb-4 text-xs text-muted-foreground">
              Agent ID: <span className="font-mono text-foreground">{agentId}</span>
            </p>
          ) : null}

          <form onSubmit={submit} className="space-y-3">
            <Field label="Name">
              <Input name="name" placeholder="Your name" autoComplete="name" />
            </Field>
            <Field label="Email">
              <Input name="email" type="email" placeholder="you@email.com" autoComplete="email" />
            </Field>
            <Field label="Message">
              <Textarea name="message" placeholder="Your message" rows={3} />
            </Field>
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="outline" onClick={close}>
                Cancel
              </Button>
              <Button type="submit">Send message</Button>
            </div>
          </form>

          <Dialog.Close asChild>
            <button
              type="button"
              aria-label="Close"
              className="absolute right-3 top-3 rounded-sm opacity-70 transition-opacity hover:opacity-100"
            >
              <X className="h-4 w-4" />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
